import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle, Trash2, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Fetches active AI memories linked to the given entity and renders a collapsible
 * warning banner. Pass ONE of { client, product, vendor }.
 */
export default function AIMemoryBanner({ client, product, vendor }) {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  const load = async () => {
    const q = client ? `client=${encodeURIComponent(client)}` : product ? `product=${encodeURIComponent(product)}` : vendor ? `vendor=${encodeURIComponent(vendor)}` : '';
    if (!q) return;
    try {
      const r = await axios.get(`${API}/ai-memory/relevant?${q}`, { withCredentials: true });
      setItems(r.data || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [client, product, vendor]);

  const resolve = async (id) => {
    try {
      await axios.patch(`${API}/ai-memory/${id}`, { status: 'resolved' }, { withCredentials: true });
      toast.success('Memory marked resolved');
      load();
    } catch { toast.error('Failed'); }
  };

  const del = async (id) => {
    try {
      await axios.delete(`${API}/ai-memory/${id}`, { withCredentials: true });
      toast.success('Memory deleted');
      load();
    } catch { toast.error('Failed'); }
  };

  if (!items.length || dismissed) return null;

  return (
    <div className="bg-amber-900/20 border border-amber-600/50 rounded-xl p-3 space-y-2" data-testid="ai-memory-banner">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 text-amber-300 text-xs font-semibold">
          <AlertTriangle size={13} /> {items.length} past issue{items.length > 1 ? 's' : ''} to remember
        </div>
        <button onClick={() => setDismissed(true)} className="text-zinc-500 hover:text-zinc-200"><X size={14} /></button>
      </div>
      {items.map(m => (
        <div key={m.id} className="bg-zinc-950/50 rounded-lg p-2.5 border-l-2 border-amber-500" data-testid={`ai-memory-item-${m.id}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-zinc-100 text-xs font-semibold truncate">{m.summary}</div>
              <div className="text-[10px] text-zinc-500 mt-0.5 capitalize">
                {m.category} · {m.severity} · {new Date(m.created_at).toLocaleDateString()}
              </div>
              {m.suggested_solution && (
                <div className="text-[11px] text-emerald-300 mt-1.5 italic">💡 {m.suggested_solution}</div>
              )}
            </div>
            <div className="flex gap-1 shrink-0">
              <button onClick={() => resolve(m.id)} title="Mark resolved" data-testid={`ai-memory-resolve-${m.id}`}
                className="p-1.5 rounded bg-zinc-800 hover:bg-green-900/50 text-zinc-400 hover:text-green-300">
                <CheckCircle size={12} />
              </button>
              <button onClick={() => del(m.id)} title="Delete" data-testid={`ai-memory-delete-${m.id}`}
                className="p-1.5 rounded bg-zinc-800 hover:bg-red-900/50 text-zinc-400 hover:text-red-300">
                <Trash2 size={12} />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
